import React, { useState, useMemo } from 'react';
import {
    Button, Dialog, DialogContent, MenuItem, Select, List, ListItem, Box, Typography, Paper
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { getWeeksOfYear } from '../../../utils/dateRange';
import { TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';

import {
    DialogTitleStyled,
    CloseIconButtonStyled,
} from '../../../styles/CalculateMoney/AssignmentCustomerModalStyles';
import {
    weekPickerBtn,
    weekPickerDialogContent,
    weekPickerHeaderBox,
    weekPickerYearSelect,
    weekPickerListBox,
    weekPickerPaper,
    weekPickerListItem,
    weekPickerListWeekText,
    weekPickerListDateText,
    weekPickerChevron,
    searchBarInputProps,
    searchBarSx,
    searchBarIcon
} from '../../../styles/CalculateMoney/Pickers/WeekPickerStyles';

const WeekPickerSearchBar = ({ value, onChange, placeholder }) => (
    <TextField
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        size="small"
        variant="outlined"
        fullWidth
        InputProps={{
            ...searchBarInputProps,
            endAdornment: (
                <InputAdornment position="end">
                    <SearchIcon sx={searchBarIcon} />
                </InputAdornment>
            ),
        }}
        sx={searchBarSx}
    />
);

export default function WeekPicker({ value, year, onChange }) {
    const { t } = useTranslation('weekPicker');
    const [open, setOpen] = useState(false);
    const [selectedYear, setSelectedYear] = useState(year || new Date().getFullYear());
    const [searchQuery, setSearchQuery] = useState('');
    const weeks = getWeeksOfYear(selectedYear);

    const filteredWeeks = useMemo(() => {
        const query = searchQuery.trim();
        if (!query) return weeks;
        return weeks.filter(w => String(w.week).includes(query));
    }, [weeks, searchQuery]);

    return (
        <>
            <Button
                variant="outlined"
                onClick={() => setOpen(true)}
                sx={weekPickerBtn}
            >
                {value
                    ? t('weekButtonLabel', {
                        week: value,
                        start: weeks[value - 1]?.start,
                        end: weeks[value - 1]?.end
                    })
                    : t('pickAWeek')}
            </Button>
            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitleStyled>
                    {t('dialogTitle')}
                    <CloseIconButtonStyled aria-label="close" onClick={() => setOpen(false)}>
                        <CloseIcon />
                    </CloseIconButtonStyled>
                </DialogTitleStyled>
                <DialogContent sx={weekPickerDialogContent}>
                    <Box sx={weekPickerHeaderBox}>
                        <Select
                            value={selectedYear}
                            onChange={e => setSelectedYear(Number(e.target.value))}
                            fullWidth
                            sx={weekPickerYearSelect}
                            size="small"
                        >
                            {Array.from({ length: 10 }, (_, i) => {
                                const y = new Date().getFullYear() - 9 + i;
                                return <MenuItem key={y} value={y}>{y}</MenuItem>;
                            })}
                        </Select>
                        <WeekPickerSearchBar
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value.replace(/[^0-9]/g, ''))}
                            placeholder={t('searchWeek')}
                        />
                    </Box>
                    <Box sx={weekPickerListBox}>
                        <List dense sx={{ p: 0 }}>
                            {filteredWeeks.length === 0 && (
                                <Typography sx={{ color: "#aaa", p: 2, textAlign: "center" }}>
                                    {t('noWeeksFound')}
                                </Typography>
                            )}
                            {filteredWeeks.map((w, i) => (
                                <Paper
                                    key={w.week}
                                    elevation={value === w.week && selectedYear === year ? 6 : 2}
                                    sx={weekPickerPaper(value === w.week && selectedYear === year)}
                                    onClick={() => {
                                        onChange(selectedYear, w.week);
                                        setOpen(false);
                                    }}
                                >
                                    <ListItem sx={weekPickerListItem}>
                                        <Box>
                                            <Typography sx={weekPickerListWeekText}>
                                                {t('weekLabel', { week: w.week })}
                                            </Typography>
                                            <Typography sx={weekPickerListDateText}>
                                                {t('weekRange', { start: w.start, end: w.end })}
                                            </Typography>
                                        </Box>
                                        <ChevronRightIcon sx={weekPickerChevron} />
                                    </ListItem>
                                </Paper>
                            ))}
                        </List>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
}